import { supabase } from '@/integrations/supabase/client'
import type { Order } from '@/types/index'
import { printKitchenTicketWithPrinter, printReceiptWithPrinter } from '@/utils/printUtils'
import { sendOrderStatusCommunication } from '@/services/communicationService'

// Centralized error extractor to ensure useful messages bubble up from Supabase/PostgREST
function extractErrorMessage(err: unknown): string {
  if (!err) return 'Unknown error'
  if (err instanceof Error) return err.message
  if (typeof err === 'object') {
    const anyErr = err as { message?: string; error?: string; error_description?: string; details?: string }
    if (anyErr.message) return anyErr.message
    if (anyErr.error_description) return anyErr.error_description
    if (anyErr.error) return anyErr.error
    if (anyErr.details) return anyErr.details
    try {
      return JSON.stringify(err)
    } catch (_) {
      return String(err)
    }
  }
  return String(err)
}

// Fire-and-forget helper for logging RPCs with a short timeout to avoid blocking UI
const LOG_TIMEOUT_MS = 1500
type Thenable<T = unknown> = { then: (onFulfilled?: (value: T) => void, onRejected?: (reason?: unknown) => void) => void }
async function fireAndForgetLog(promise: Promise<unknown> | Thenable): Promise<void> {
  try {
    // Race the RPC against a timeout. We do not care about its result.
    await Promise.race([
      // Coerce PostgrestFilterBuilder-like thenables to a real Promise
      (promise instanceof Promise ? promise : new Promise((resolve, reject) => {
        ;(promise as Thenable).then(resolve, reject)
      })),
      new Promise<void>((resolve) => setTimeout(resolve, LOG_TIMEOUT_MS))
    ])
  } catch (_) {
    // Swallow logging errors completely
  }
}

export const updateOrderStatus = async (orderId: string, status: string, isStaffOrPaymentMethod: boolean | string = false, paymentMethod?: string) => {
  try {
    // Handle parameter overloading
    let isStaff = false;
    let actualPaymentMethod: string | undefined;
    
    if (typeof isStaffOrPaymentMethod === 'boolean') {
      isStaff = isStaffOrPaymentMethod;
      actualPaymentMethod = paymentMethod;
    } else {
      // isStaffOrPaymentMethod is actually a payment method string
      actualPaymentMethod = isStaffOrPaymentMethod;
    }

    // Prepare update data
    const updateData: {
      status: string;
      updated_at: string;
      payment_method?: string;
    } = {
      status,
      updated_at: new Date().toISOString()
    };
    
    // Add payment method if provided (for completed orders)
    if (actualPaymentMethod && status === 'completed') {
      updateData.payment_method = actualPaymentMethod;
    }

    // Use RPC to update order status (bypasses RLS for staff users)
    // This is needed because staff users authenticate via localStorage, not Supabase auth
    const { data, error } = await supabase.rpc('update_order_status_rpc', {
      p_order_id: orderId,
      p_status: status,
      p_payment_method: actualPaymentMethod || null
    });

    if (error) {
      console.error('Error updating order status:', error)
      throw error
    }

    // Check if RPC returned an error
    if (data && typeof data === 'object' && 'success' in data && !data.success) {
      console.error('RPC returned error:', data);
      throw new Error((data as { error?: string }).error || 'Failed to update order status');
    }

    // Fire-and-forget side effects for inventory and loyalty
    void runOrderStatusSideEffects({ orderId, nextStatus: status })

    return { success: true }
  } catch (err: unknown) {
    console.error('Error updating order status:', err)
    const errorMessage = err instanceof Error ? err.message : String(err)
    return { success: false, error: errorMessage }
  }
}

export const updateOrderItem = async (
  orderItemId: string, 
  updates: { quantity?: number; notes?: string; is_acknowledged?: boolean },
  isStaff: boolean = false
) => {
  try {
    // Get the current user ID for tracking who made the modification
    const { data: { user } } = await supabase.auth.getUser()
    const modifiedBy = user?.id

    // Get the order item details before updating
    const { data: oldItem } = await supabase
      .from('order_items')
      .select('*')
      .eq('id', orderItemId)
      .single()

    if (!oldItem) {
      throw new Error('Order item not found')
    }

    const { data: updatedItem, error } = await supabase
      .from('order_items')
      .update(updates)
      .eq('id', orderItemId)
      .select()
      .single()

    if (error) {
      console.error('Error updating order item:', error)
      throw error
    }

    // Log the modification (best-effort, non-blocking)
    if (updatedItem) {
      let modificationType = 'notes_changed'
      if (updates.quantity !== undefined && updates.quantity !== oldItem.quantity) {
        modificationType = 'quantity_changed'
      } else if (updates.is_acknowledged !== undefined && updates.is_acknowledged !== oldItem.is_acknowledged) {
        modificationType = 'notes_changed'
      }
      void fireAndForgetLog(
        supabase.rpc('log_order_item_modification', {
          p_order_item_id: orderItemId,
          p_order_id: oldItem.order_id,
          p_modification_type: modificationType,
          p_old_value: oldItem,
          p_new_value: updatedItem,
          p_modified_by: modifiedBy,
          p_notes: `Item ${modificationType.replace('_', ' ')}`
        })
      )
    }
    
    return { success: true }
  } catch (err: unknown) {
    console.error('Error updating order item:', err)
    const errorMessage = err instanceof Error ? err.message : String(err)
    return { success: false, error: errorMessage }
  }
}

interface RunSideEffectsParams {
  orderId: string
  nextStatus: string
}

async function runOrderStatusSideEffects({ orderId, nextStatus }: RunSideEffectsParams) {
  try {
    // Fetch order with items and branch info for side effects
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select(`
        id,
        status,
        branch_id,
        customer_id,
        total_amount,
        order_number,
        customer_name,
        customer_phone,
        customer_address,
        notes,
        order_type,
        table_number,
        delivery_fee,
        created_at,
        order_items (
          id,
          menu_item_id,
          menu_item_variant_id,
          quantity,
          unit_price,
          total_price,
          notes,
          is_removed,
          menu_items (
            name,
            description,
            assigned_printer_name
          )
        ),
        branches (
          default_kitchen_printer_name,
          default_receipt_printer_name,
          auto_print_kitchen_on_confirm,
          auto_print_receipt_on_confirm,
          auto_print_receipt_on_complete,
          name,
          address,
          phone,
          currency,
          restaurants (
            name,
            logo_url
          )
        )
      `)
      .eq('id', orderId)
      .single()

    if (orderError || !order) return

    // 1) Automatic Printing on status changes
    if (nextStatus === 'confirmed' && order.branches?.auto_print_kitchen_on_confirm) {
      await handleAutomaticKitchenPrint(order as any)
    }

    // 2) Automatic Communication on status changes
    try {
      if (['confirmed', 'preparing', 'ready', 'completed'].includes(nextStatus)) {
        await sendOrderStatusCommunication(orderId, nextStatus, 'sms');
      }
    } catch (commError) {
      console.error('Communication send failed (non-blocking):', commError);
      // Don't throw error to avoid breaking order flow
    }

    // Auto-print receipt on confirm (requested by user)
    if (nextStatus === 'confirmed' && order.branches?.auto_print_receipt_on_confirm) {
      await handleAutomaticReceiptPrint(order as any)
    }

    if (nextStatus === 'completed' && order.branches?.auto_print_receipt_on_complete) {
      await handleAutomaticReceiptPrint(order as any)
    }

    // 2) Inventory deduction on move to preparing or completed (real-world: consumption starts when kitchen begins or when order is completed)
    if (nextStatus === 'preparing' || nextStatus === 'completed') {
      try {
        const items = (order.order_items || []).map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity
        }))

        if (items.length > 0) {
          // Simple inventory deduction without complex RPC
          await deductInventoryForOrder(orderId)
        }
      } catch (invErr) {
        console.error('Inventory deduction failed:', invErr)
      }
    }

    // 3) Loyalty points: award on completed; reverse on cancelled (real-world settlement)
    if (order.customer_id) {
      if (nextStatus === 'completed') {
        await awardLoyaltyPoints({
          orderId,
          customerId: order.customer_id,
          branchId: order.branch_id,
          orderTotal: order.total_amount || 0
        })
      }

      if (nextStatus === 'cancelled') {
        // Only try to reverse if the order was previously completed
        if (order.status === 'completed' || order.status === 'ready') {
          await reverseLoyaltyPoints({ orderId, customerId: order.customer_id })
        }
      }
    }
  } catch (e) {
    console.error('runOrderStatusSideEffects error:', e)
  }
}

async function awardLoyaltyPoints(params: { orderId: string; customerId: string; branchId: string; orderTotal: number }) {
  const { orderId, customerId, branchId, orderTotal } = params
  try {
    // Find restaurant for branch
    const { data: branch, error: branchErr } = await supabase
      .from('branches')
      .select('restaurant_id')
      .eq('id', branchId)
      .single()
    if (branchErr || !branch?.restaurant_id) return

    // Get active program
    const { data: program } = await supabase
      .from('loyalty_programs')
      .select('*')
      .eq('restaurant_id', branch.restaurant_id)
      .eq('is_active', true)
      .single()
    if (!program) return

    const pointsPerDollar = Number(program.points_per_dollar ?? 1)
    const points = Math.floor(pointsPerDollar * Math.max(0, orderTotal))
    if (points <= 0) return

    // Idempotency: check if there is already an earned txn for this order
    const { data: existing } = await supabase
      .from('loyalty_transactions')
      .select('id')
      .eq('customer_id', customerId)
      .eq('restaurant_id', branch.restaurant_id)
      .eq('order_id', orderId)
      .eq('transaction_type', 'earned')
      .limit(1)
      .maybeSingle()
    if (existing) return

    // Create transaction
    await supabase.from('loyalty_transactions').insert({
      customer_id: customerId,
      restaurant_id: branch.restaurant_id,
      order_id: orderId,
      transaction_type: 'earned',
      points,
      description: `Points earned for order ${orderId}`
    })

    // Upsert points
    const { data: lp } = await supabase
      .from('customer_loyalty_points')
      .select('*')
      .eq('customer_id', customerId)
      .eq('restaurant_id', branch.restaurant_id)
      .single()

    if (!lp) {
      await supabase.from('customer_loyalty_points').insert({
        customer_id: customerId,
        restaurant_id: branch.restaurant_id,
        current_points: points,
        total_earned_points: points,
        total_redeemed_points: 0
      })
    } else {
      await supabase
        .from('customer_loyalty_points')
        .update({
          current_points: (lp.current_points || 0) + points,
          total_earned_points: (lp.total_earned_points || 0) + points
        })
        .eq('id', lp.id)
    }
  } catch (e) {
    console.error('awardLoyaltyPoints error:', e)
  }
}

async function deductInventoryForOrder(orderId: string) {
  try {
    console.log(`🔄 Starting secure inventory deduction for order: ${orderId}`)
    
    // Get current user context to determine authentication method
    const { data: { user } } = await supabase.auth.getUser()
    
    // Check if we have a staff session in localStorage
    const staffSessionStr = localStorage.getItem('staff_session')
    let staffSession = null
    if (staffSessionStr) {
      try {
        staffSession = JSON.parse(staffSessionStr)
      } catch (e) {
        console.warn('Failed to parse staff session from localStorage')
      }
    }
    
    // Prepare RPC parameters
    const rpcParams: { p_order_id: string; p_staff_user_id?: string } = {
      p_order_id: orderId
    }
    
    // If we have a staff session but no regular user, pass staff user ID
    if (staffSession && !user) {
      rpcParams.p_staff_user_id = staffSession.id
      console.log(`🔧 Using staff authentication for user: ${staffSession.username} (${staffSession.id})`)
    } else if (user) {
      console.log(`🔧 Using regular user authentication for user: ${user.id}`)
    } else {
      console.warn('⚠️ No authentication context available')
    }
    
    // Call the secure RPC function with proper authentication context
    const { data, error } = await supabase.rpc('inventory_deduct_for_order_with_staff', rpcParams)
    
    if (error) {
      console.error('❌ RPC function error:', error)
      throw error
    }
    
    if (!data) {
      console.warn('⚠️ No data returned from RPC function')
      return
    }
    
    // Check if the operation was successful
    if (data.success) {
      console.log('✅ Inventory deduction successful:', {
        orderId: data.order_id,
        materialsProcessed: data.materials_processed,
        message: data.message,
        processedBy: data.processed_by,
        userContext: data.user_context
      })
      
      // Log details if materials were deducted
      if (data.materials_deducted && Array.isArray(data.materials_deducted) && data.materials_deducted.length > 0) {
        console.log('📦 Materials deducted:', data.materials_deducted)
      } else {
        console.log('ℹ️ No materials were deducted (no recipes configured or inventory already deducted)')
      }
    } else {
      // RPC function returned an error
      console.error('❌ Inventory deduction failed:', data.error)
      if (data.debug_info) {
        console.error('🔍 Debug info:', data.debug_info)
      }
      throw new Error(data.error || 'Unknown error during inventory deduction')
    }
    
  } catch (error) {
    console.error('💥 deductInventoryForOrder error:', error)
    // Don't throw - this is a side effect that shouldn't break order status updates
    // But log it clearly for debugging
  }
}

async function reverseLoyaltyPoints(params: { orderId: string; customerId: string }) {
  const { orderId, customerId } = params
  try {
    const { data: txn, error: txnError } = await supabase
      .from('loyalty_transactions')
      .select('*')
      .eq('customer_id', customerId)
      .eq('order_id', orderId)
      .eq('transaction_type', 'earned')
      .maybeSingle()
    if (txnError) {
      console.error('Error finding earned transaction:', txnError)
      return
    }
    if (!txn) {
      console.log('No earned transaction found for order', orderId, '- order was likely not completed, skipping reversal')
      return
    }

    // Check if already reversed
    const { data: existingReversal } = await supabase
      .from('loyalty_transactions')
      .select('id')
      .eq('customer_id', customerId)
      .eq('restaurant_id', txn.restaurant_id)
      .eq('order_id', orderId)
      .eq('transaction_type', 'redeemed')
      .ilike('description', 'Reversal%')
      .limit(1)
      .maybeSingle()
    if (existingReversal) return

    // Insert reversal record
    await supabase.from('loyalty_transactions').insert({
      customer_id: txn.customer_id,
      restaurant_id: txn.restaurant_id,
      order_id: orderId,
      transaction_type: 'redeemed',
      points: txn.points,
      description: `Reversal of earned points for cancelled order ${orderId}`
    })

    // Decrement user points
    const { data: lp } = await supabase
      .from('customer_loyalty_points')
      .select('*')
      .eq('customer_id', customerId)
      .eq('restaurant_id', txn.restaurant_id)
      .single()
    if (!lp) return

    await supabase
      .from('customer_loyalty_points')
      .update({ current_points: Math.max(0, (lp.current_points || 0) - (txn.points || 0)) })
      .eq('id', lp.id)
  } catch (e) {
    console.error('reverseLoyaltyPoints error:', e)
  }
}

// Super optimized batch function for ALL order operations
export const batchAllOrderOperations = async (
  operations: {
    updates?: Array<{
      orderItemId: string
      updates: { quantity?: number; notes?: string; is_acknowledged?: boolean }
    }>
    creates?: Array<{
      orderId: string
      menuItemId: string
      quantity: number
      unitPrice: number
      notes?: string
      variantId?: string
      branchId?: string
    }>
    removes?: Array<{
      orderItemId: string
    }>
    restores?: Array<{
      orderItemId: string
    }>
  },
  isStaff: boolean = false
) => {
  try {
    console.log('🚀 Starting super batch operations...')
    const startTime = Date.now()

    // Get the current user ID once
    const { data: { user } } = await supabase.auth.getUser()
    const modifiedBy = user?.id

    // Collect all order item IDs that need to be fetched
    const allItemIds = [
      ...(operations.updates?.map(u => u.orderItemId) || []),
      ...(operations.removes?.map(r => r.orderItemId) || []),
      ...(operations.restores?.map(r => r.orderItemId) || [])
    ]

    // Sanitize IDs (filter out null/undefined/empty and deduplicate)
    const uniqueValidItemIds = Array.from(
      new Set(
        allItemIds.filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    )

    // Fetch all existing items in one query (if needed)
    interface ExistingOrderItem { id: string; order_id: string; unit_price?: number; quantity: number; is_acknowledged?: boolean }
    let existingItemsMap = new Map<string, ExistingOrderItem>()
    if (uniqueValidItemIds.length > 0) {
      const { data: items, error: fetchError } = await supabase
        .from('order_items')
        .select('*')
        .in('id', uniqueValidItemIds)

      if (fetchError) throw fetchError
      existingItemsMap = new Map((items as ExistingOrderItem[] | undefined)?.map((item) => [item.id, item]) || [])
    }

    console.log(`⏱️ Fetched ${uniqueValidItemIds.length} items in ${Date.now() - startTime}ms`)

    // Prepare ALL operations to run in parallel
    const allPromises: Promise<Record<string, unknown>>[] = []

    // 1. Handle updates
    if (operations.updates?.length) {
      const updatePromises = operations.updates.map(async ({ orderItemId, updates: itemUpdates }) => {
        const oldItem = existingItemsMap.get(orderItemId)
        if (!oldItem) throw new Error(`Order item ${orderItemId} not found`)

        const nextQuantity = typeof itemUpdates.quantity === 'number' ? itemUpdates.quantity : oldItem.quantity
        const computedTotal = (oldItem.unit_price ?? 0) * (nextQuantity ?? 0)

        const { data: updatedItem, error } = await supabase
          .from('order_items')
          .update({
            ...itemUpdates,
            total_price: computedTotal,
            updated_at: new Date().toISOString(),
          })
          .eq('id', orderItemId)
          .select()
          .single()

        if (error) throw error
        return { type: 'update', oldItem, updatedItem, orderItemId }
      })
      allPromises.push(...updatePromises)
    }

    // 2. Handle creates
    if (operations.creates?.length) {
      const createPromises = operations.creates.map(async (createOp) => {
        // Determine branch_id: prefer provided branchId, otherwise fetch from order
        let targetBranchId = createOp.branchId
        if (!targetBranchId) {
          const { data: orderRow, error: orderFetchError } = await supabase
            .from('orders')
            .select('branch_id')
            .eq('id', createOp.orderId)
            .single()
          if (orderFetchError) throw orderFetchError
          targetBranchId = orderRow?.branch_id
        }

        const { data: newItem, error } = await supabase
          .from('order_items')
          .insert({
            order_id: createOp.orderId,
            branch_id: targetBranchId,
            menu_item_id: createOp.menuItemId,
            quantity: createOp.quantity,
            unit_price: createOp.unitPrice,
            total_price: createOp.unitPrice * createOp.quantity,
            notes: createOp.notes,
            menu_item_variant_id: createOp.variantId || null,
            updated_at: new Date().toISOString(),
          })
          .select()
          .single()

        if (error) throw error
        return { type: 'create', newItem, orderId: createOp.orderId }
      })
      allPromises.push(...createPromises)
    }

    // 3. Handle removes (soft delete)
    if (operations.removes?.length) {
      const removePromises = operations.removes.map(async ({ orderItemId }) => {
        const oldItem = existingItemsMap.get(orderItemId)
        if (!oldItem) throw new Error(`Order item ${orderItemId} not found`)

        const { error } = await supabase
          .from('order_items')
          .update({
            is_removed: true,
            removed_at: new Date().toISOString(),
            removed_by: modifiedBy,
            updated_at: new Date().toISOString(),
          })
          .eq('id', orderItemId)

        if (error) throw error
        return { type: 'remove', oldItem, orderItemId }
      })
      allPromises.push(...removePromises)
    }

    // 4. Handle restores
    if (operations.restores?.length) {
      const restorePromises = operations.restores.map(async ({ orderItemId }) => {
        const oldItem = existingItemsMap.get(orderItemId)
        if (!oldItem) throw new Error(`Order item ${orderItemId} not found`)

        const { error } = await supabase
          .from('order_items')
          .update({
            is_removed: false,
            removed_at: null,
            removed_by: null,
            updated_at: new Date().toISOString(),
          })
          .eq('id', orderItemId)

        if (error) throw error
        return { type: 'restore', oldItem, orderItemId }
      })
      allPromises.push(...restorePromises)
    }

    // Execute ALL operations in parallel
    console.log(`🔄 Executing ${allPromises.length} operations in parallel...`)
    const operationStart = Date.now()
    const results = await Promise.all(allPromises)
    console.log(`⏱️ All operations completed in ${Date.now() - operationStart}ms`)

    // Log all modifications in parallel
    console.log(`📝 Queuing ${results.length} modification logs (best-effort)...`)
    const logPromises = results.map((result: Record<string, unknown>) => {
      if (result.type === 'update') {
        const updateResult = result as { 
          type: string; 
          orderItemId: string; 
          oldItem: { order_id: string; quantity: number; is_acknowledged?: boolean }; 
          updatedItem: { quantity: number; is_acknowledged?: boolean } 
        }
        let modificationType = 'notes_changed'
        if (updateResult.updatedItem.quantity !== updateResult.oldItem.quantity) {
          modificationType = 'quantity_changed'
        } else if (updateResult.updatedItem.is_acknowledged !== updateResult.oldItem.is_acknowledged) {
          modificationType = 'notes_changed'
        }

        return supabase.rpc('log_order_item_modification', {
          p_order_item_id: updateResult.orderItemId,
          p_order_id: updateResult.oldItem.order_id,
          p_modification_type: modificationType,
          p_old_value: updateResult.oldItem,
          p_new_value: updateResult.updatedItem,
          p_modified_by: modifiedBy,
          p_notes: `Item ${modificationType.replace('_', ' ')}`
        })
      } else if (result.type === 'create') {
        const createResult = result as { 
          type: string; 
          newItem: { id: string }; 
          orderId: string 
        }
        return supabase.rpc('log_order_item_modification', {
          p_order_item_id: createResult.newItem.id,
          p_order_id: createResult.orderId,
          p_modification_type: 'added',
          p_new_value: createResult.newItem,
          p_modified_by: modifiedBy,
          p_notes: 'Item added to order'
        })
      } else if (result.type === 'remove') {
        const removeResult = result as { 
          type: string; 
          orderItemId: string; 
          oldItem: { order_id: string } 
        }
        return supabase.rpc('log_order_item_modification', {
          p_order_item_id: removeResult.orderItemId,
          p_order_id: removeResult.oldItem.order_id,
          p_modification_type: 'removed',
          p_old_value: removeResult.oldItem,
          p_modified_by: modifiedBy,
          p_notes: 'Item removed from order'
        })
      } else if (result.type === 'restore') {
        const restoreResult = result as { 
          type: string; 
          orderItemId: string; 
          oldItem: { order_id: string } 
        }
        return supabase.rpc('log_order_item_modification', {
          p_order_item_id: restoreResult.orderItemId,
          p_order_id: restoreResult.oldItem.order_id,
          p_modification_type: 'restored',
          p_old_value: restoreResult.oldItem,
          p_modified_by: modifiedBy,
          p_notes: 'Item restored to order'
        })
      }
    })
    // Fire-and-forget with timeout; do not block response time
    logPromises.filter(Boolean).forEach(p => { void fireAndForgetLog(p as unknown as Promise<unknown>) })
    console.log(`✅ Super batch operations completed in ${Date.now() - startTime}ms (logs queued) `)
    
    return { success: true }
  } catch (err: unknown) {
    console.error('Error in super batch operations:', err)
    const errorMessage = extractErrorMessage(err)
    return { success: false, error: errorMessage }
  }
}

// Keep the original batch update function for backward compatibility
export const batchUpdateOrderItems = async (
  updates: Array<{
    orderItemId: string
    updates: { quantity?: number; notes?: string; is_acknowledged?: boolean }
  }>,
  isStaff: boolean = false
) => {
  return batchAllOrderOperations({ updates }, isStaff)
}

export const deleteOrderItem = async (orderItemId: string, isStaff: boolean = false) => {
  try {
    // Get the current user ID for tracking who made the modification
    const { data: { user } } = await supabase.auth.getUser()
    const modifiedBy = user?.id

    // Get the order item details before marking as removed
    const { data: orderItem } = await supabase
      .from('order_items')
      .select('*')
      .eq('id', orderItemId)
      .single()

    if (!orderItem) {
      throw new Error('Order item not found')
    }

    // Mark as removed instead of deleting (soft delete)
    const { error: updateError } = await supabase
      .from('order_items')
      .update({
        is_removed: true,
        removed_at: new Date().toISOString(),
        removed_by: modifiedBy
      })
      .eq('id', orderItemId)

    if (updateError) {
      console.error('Error marking order item as removed:', updateError)
      throw updateError
    }

    // Log the modification (non-blocking)
    void fireAndForgetLog(
      supabase.rpc('log_order_item_modification', {
        p_order_item_id: orderItemId,
        p_order_id: orderItem.order_id,
        p_modification_type: 'removed',
        p_old_value: orderItem,
        p_modified_by: modifiedBy,
        p_notes: 'Item removed from order'
      })
    )
    
    return { success: true }
  } catch (err: unknown) {
    console.error('Error removing order item:', err)
    const errorMessage = err instanceof Error ? err.message : String(err)
    return { success: false, error: errorMessage }
  }
}

export const createOrderItem = async (
  orderId: string,
  menuItemId: string,
  quantity: number,
  unitPrice: number,
  notes?: string,
  variantId?: string,
  isStaff: boolean = false
) => {
  try {
    // Get the current user ID for tracking who made the modification
    const { data: { user } } = await supabase.auth.getUser()
    const modifiedBy = user?.id

    // Get the branch_id from the order since it's required for order_items
    const { data: order, error: orderError } = await supabase
      .from('orders')
      .select('branch_id')
      .eq('id', orderId)
      .single()

    if (orderError || !order) {
      throw new Error('Order not found or unable to fetch branch_id')
    }

    const { data: newItem, error } = await supabase
      .from('order_items')
      .insert({
        order_id: orderId,
        branch_id: order.branch_id,
        menu_item_id: menuItemId,
        quantity,
        unit_price: unitPrice,
        total_price: unitPrice * quantity,
        notes,
        menu_item_variant_id: variantId
      })
      .select()
      .single()

    if (error) {
      console.error('Error creating order item:', error)
      throw error
    }

    // Log the modification (non-blocking)
    if (newItem) {
      void fireAndForgetLog(
        supabase.rpc('log_order_item_modification', {
          p_order_item_id: newItem.id,
          p_order_id: orderId,
          p_modification_type: 'added',
          p_new_value: newItem,
          p_modified_by: modifiedBy,
          p_notes: 'Item added to order'
        })
      )
    }
    
    return { success: true }
  } catch (err: unknown) {
    console.error('Error creating order item:', err)
    const errorMessage = err instanceof Error ? err.message : String(err)
    return { success: false, error: errorMessage }
  }
}

export const restoreOrderItem = async (orderItemId: string, isStaff: boolean = false) => {
  try {
    // Get the current user ID for tracking who made the modification
    const { data: { user } } = await supabase.auth.getUser()
    const modifiedBy = user?.id

    // Get the order item details before restoring
    const { data: orderItem } = await supabase
      .from('order_items')
      .select('*')
      .eq('id', orderItemId)
      .single()

    if (!orderItem) {
      throw new Error('Order item not found')
    }

    // Restore the item by setting is_removed to false
    const { error: updateError } = await supabase
      .from('order_items')
      .update({
        is_removed: false,
        removed_at: null,
        removed_by: null
      })
      .eq('id', orderItemId)

    if (updateError) {
      console.error('Error restoring order item:', updateError)
      throw updateError
    }

    // Log the modification (non-blocking)
    void fireAndForgetLog(
      supabase.rpc('log_order_item_modification', {
        p_order_item_id: orderItemId,
        p_order_id: orderItem.order_id,
        p_modification_type: 'restored',
        p_old_value: orderItem,
        p_modified_by: modifiedBy,
        p_notes: 'Item restored to order'
      })
    )
    
    return { success: true }
  } catch (err: unknown) {
    console.error('Error restoring order item:', err)
    const errorMessage = err instanceof Error ? err.message : String(err)
    return { success: false, error: errorMessage }
  }
}

export const getOrderModifications = async (orderId: string) => {
  try {
    const { data, error } = await supabase
      .from('order_item_modifications')
      .select(`
        *,
        order_items (
          id,
          menu_item_id,
          quantity,
          unit_price,
          notes,
          menu_items (
            id,
            name,
            description
          )
        )
      `)
      .eq('order_id', orderId)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching order modifications:', error)
      throw error
    }
    
    return { success: true, data }
  } catch (err: unknown) {
    console.error('Error fetching order modifications:', err)
    const errorMessage = err instanceof Error ? err.message : String(err)
    return { success: false, error: errorMessage }
  }
}

// Automatic Printing Handler Functions
async function handleAutomaticKitchenPrint(order: any) {
  try {
    console.log('🖨️ Auto-printing kitchen ticket for order:', order.order_number)
    
    const branchInfo = {
      name: order.branches?.restaurants?.name || order.branches?.name || "Menu Maven Restaurant"
    }

    // Group items by assigned printer
    const itemsByPrinter = new Map<string, any[]>()
    
    for (const item of order.order_items || []) {
      if (item.is_removed) continue

      const printerName = item.menu_items?.assigned_printer_name || 
                         order.branches?.default_kitchen_printer_name || 
                         'default'
      
      if (!itemsByPrinter.has(printerName)) {
        itemsByPrinter.set(printerName, [])
      }
      itemsByPrinter.get(printerName)!.push(item)
    }

    // Print separate tickets for each printer
    for (const [printerName, items] of itemsByPrinter) {
      const orderForPrinter = {
        ...order,
        order_items: items
      }
      
      await printKitchenTicketWithPrinter(orderForPrinter, branchInfo, printerName, true)
    }
  } catch (error) {
    console.error('Auto kitchen print failed:', error)
    // Don't throw error to avoid breaking order flow
  }
}

async function handleAutomaticReceiptPrint(order: any) {
  try {
    console.log('🖨️ Auto-printing receipt for order:', order.order_number)
    
    const branchInfo = {
      name: order.branches?.restaurants?.name || order.branches?.name || "Menu Maven Restaurant",
      address: order.branches?.address || "Restaurant Address",
      phone: order.branches?.phone || "Restaurant Phone",
      logo: order.branches?.restaurants?.logo_url || undefined,
      currency: order.branches?.currency || 'USD'
    }

    const printerName = order.branches?.default_receipt_printer_name || 'default'
    const receiptItems = (order.order_items || []) as Array<{ is_removed?: boolean | null }>
    const receiptOrder = {
      ...order,
      order_items: receiptItems.filter(item => !item.is_removed)
    }
    
    // Add user-friendly notification about auto-printing
    console.log('🖨️ Attempting automatic receipt print...')
    
    // Use a small delay to ensure the UI state has settled before printing
    setTimeout(async () => {
      try {
        await printReceiptWithPrinter(receiptOrder, branchInfo, printerName, true)
        console.log('✅ Auto-print receipt completed successfully')
      } catch (printError) {
        console.error('❌ Auto-print receipt failed:', printError)
        // Show user notification about printing failure
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('print-receipt-failed', {
            detail: { 
              orderId: order.id, 
              orderNumber: order.order_number,
              reason: printError instanceof Error ? printError.message : 'Print failed'
            }
          }))
        }
      }
    }, 500) // 500ms delay to ensure order confirmation UI has updated
    
  } catch (error) {
    console.error('Auto receipt print setup failed:', error)
    // Don't throw error to avoid breaking order flow
  }
}
